import { ref } from 'vue'
import { ICON_DEFS } from '~/data/maquette'
import { ENTITY_DEFS } from '~/data/entities'
import { COMPUTE_FNS, DEFAULT_FNS, DISPLAY_FNS, FILTER_FNS } from '~/utils/entityFns'
import { fieldsOf } from '~/utils/entityColumns'
import { entityRows as entityRowsByKey } from '~/composables/useDb'
import type { Row } from '~/composables/useDb'

/**
 * Ce que le code repris de la maquette (app/legacy/screens.ts) attend de son environnement : `db`, `ENTITIES`, `icon`,
 * `render`, `currentEcoleId`, `entityRows`, `todayISO`, `closeModal`. Ici, ils sont branchés sur l'application Nuxt.
 */

/** Nombre incrémenté à chaque `render()` de la maquette : les pages recalculent alors leur contenu. */
export const renderTick = ref(0)
export function render(): void {
  renderTick.value++
}

/** `currentEcoleId` de la maquette (liaison vivante, mise à jour avant chaque rendu). */
// eslint-disable-next-line import/no-mutable-exports -- le code de la maquette lit `currentEcoleId` comme une variable : liaison vivante nécessaire
export let currentEcoleId: string | null = null
export function syncLegacyState(): void {
  currentEcoleId = useCurrentEcoleId().value
}

/** `db` de la maquette : les collections chargées depuis l'API Java. */
export const db = new Proxy({} as Record<string, Row[]>, { get: (_t, k) => useDb().value[k as string] ?? [] })

/** `icon(name, extraClass)` de la maquette. */
export function icon(name: string, extraClass?: string): string {
  if (!name || !ICON_DEFS[name]) return ''
  return `<svg viewBox="0 0 24 24" class="icon ${extraClass || ''}" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">${ICON_DEFS[name]}</svg>`
}

/** `ENTITIES` de la maquette : libellés, champs (avec leurs fonctions display / filter), clé de stockage. */
const legacyCache: Record<string, unknown> = {}
function legacyEntity(key: string) {
  const def = ENTITY_DEFS[key]
  if (!def) return undefined
  return (legacyCache[key] ??= {
    key, label: def.label, labelPlural: def.labelPlural, module: def.module, icon: def.icon, storageKey: def.storageKey,
    fields: fieldsOf(key).map(f => ({
      ...f,
      display: f.displayFn ? DISPLAY_FNS[`${key}.${f.key}`] : f.display,
      filter: f.filterFn ? FILTER_FNS[`${key}.${f.key}`] : undefined,
      computeValue: f.computeValue ? COMPUTE_FNS[`${key}.${f.key}`] : undefined,
      default: f.defaultFn ? DEFAULT_FNS[`${key}.${f.key}`] : f.default,
    })),
  })
}
export const ENTITIES = new Proxy({} as Record<string, unknown>, {
  get: (_t, k) => (typeof k === 'string' ? legacyEntity(k) : undefined),
  has: (_t, k) => typeof k === 'string' && !!ENTITY_DEFS[k],
})

/** `entityRows(entity)` de la maquette : elle reçoit l'objet entité. */
export function entityRows(entity: { key: string }): Row[] {
  return entityRowsByKey(entity.key)
}

export function todayISO(): string {
  return new Date().toISOString().slice(0, 10)
}

/** `closeModal` de la maquette. */
export function closeModal(): void {
  document.getElementById('modal-overlay')?.remove()
  useUeResumePrefill().value = null
}

/** Valeurs du formulaire « Nouvelle UE » en attente de reprise après la création d'une matière depuis ce même formulaire. */
export function useUeResumePrefill() {
  return useState<Record<string, unknown> | null>('ueResumePrefill', () => null)
}

/** Pont vers `useRecuInscriptionContext` (composables/useDocuments) : la maquette appelle `openRecuInscription` avant `goTo('recu-inscription')`, la page lit ce contexte réactif. */
export function setRecuInscriptionContext(ctx: { inscriptionId: string, versementIds: string[] } | null): void {
  useRecuInscriptionContext().value = ctx
}

/** `tablePage` et `tableSearch` de la maquette : page et recherche de chaque liste (état partagé avec les pages de liste). */
export const tablePage = new Proxy({} as Record<string, number>, {
  get: (_t, k) => useTable().page.value[k as string],
  set: (_t, k, v) => {
    const t = useTable()
    t.page.value = { ...t.page.value, [k as string]: v as number }
    return true
  },
})
export const tableSearch = new Proxy({} as Record<string, string>, {
  get: (_t, k) => useTable().search.value[k as string],
  set: (_t, k, v) => {
    const t = useTable()
    t.search.value = { ...t.search.value, [k as string]: v as string }
    return true
  },
})
